<template>
  <div @click.self="closeModal" class="modalContainer">
    <div class="modalWrapper">
      <div @click="closeModal" class="close">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M8.1161 6.6228L7.75908 6.97635L8.11437 7.33163L13.4572 12.6745L12.6745 13.4572L11.8466 12.6294L11.8449 12.6277L7.37718 8.11608L7.02364 7.75906L6.66835 8.11435L1.32551 13.4572L0.542786 12.6745L5.88563 7.33163L6.24062 6.97664L5.8842 6.62309L0.543158 1.32514L1.32551 0.542787L6.66835 5.88563L7.02334 6.24061L7.37689 5.88419L12.6748 0.543155L13.4572 1.32551L12.6294 2.15333L12.6277 2.15504L8.1161 6.6228ZM12.7648 13.5475C12.7649 13.5476 12.765 13.5478 12.7651 13.5479L12.7648 13.5475ZM0.452113 12.7651L0.452145 12.7651C0.452134 12.7651 0.452123 12.7651 0.452113 12.7651Z"
            fill="black"
            stroke="black"
          />
        </svg>
      </div>
      <h2>{{ phoneInputVisible ? t('phoneVerification.changePhoneTitle') : t('phoneVerification.verifyPhoneTitle') }}</h2>
      <p v-if="phoneInputVisible" class="description">
        {{ t('phoneVerification.changePhoneDescription') }}
        <span class="clickHere" @click="togglePhoneInput">{{ t('phoneVerification.clickHere') }}</span>
      </p>
      <p v-else class="description">
        {{ t('phoneVerification.pinSentTo') }}
        <span class="bold">{{ formatPhoneNumber(customerData.phone) }}</span>
        {{ t('phoneVerification.belowToValidate') }}
        <span class="clickHere" @click="togglePhoneInput">{{ t('phoneVerification.clickHere') }}</span>
      </p>
      <div v-if="phoneInputVisible" class="phoneChange">
        <div class="inputWrapper">
          <template v-if="enableInternationalPhoneInput">
            <IntlTelInput
              ref="intlPhoneInput"
              v-model="phone"
              id="phone"
              :label="t('phoneVerification.cellPhone')"
              :placeholder="t('phoneVerification.cellPhone')"
              :initial-country="customerData?.country_code ? customerData.country_code.toLowerCase() : 'us'"
              :use-validation="true"
              :required="true"
              @countryChange="handleCountryChange"
            />
          </template>
          <template v-else>
            <label for="phone">{{ t('phoneVerification.cellPhone') }}</label>
            <input
              @input="formatPhoneNumberWhileTyping(phone)"
              v-model="phone"
              type="text"
              :placeholder="t('phoneVerification.cellPhone')"
              id="phone"
              inputmode="tel"
            />
            <p v-if="v$.phone.$error === true" class="validation">{{ t('phoneVerification.validation.enterPhone') }}</p>
            <p v-if="validPhoneNumberError" class="validation" data-cy="phone-validation-error">
              {{ validPhoneNumberErrorMessage }}
            </p>
          </template>
        </div>
        <p class="feedback error" v-if="error.status && phoneInputVisible">
          {{ error.message }}
        </p>
        <button @click="changePhone">
          <div v-if="buttonLoader" class="loader buttonLoader"></div>
          <template v-if="!buttonLoader">{{ t('phoneVerification.update') }}</template>
        </button>
      </div>
      <div v-if="!phoneInputVisible" class="pin">
        <Verification
          type="text"
          activeInputClass="active-input-class"
          @on-complete="onCompleteHandler"
          @on-paste="onPasteHandler"
        />
      </div>
      <p v-if="!codeIsValid && !phoneInputVisible" class="validation">{{ t('phoneVerification.invalidPin') }}</p>
      <a @click="sendVerificationCode(true)" class="resend" href="javascript:;" v-if="!phoneInputVisible">
        <p>{{ t('phoneVerification.resendPin') }}</p>
      </a>
      <p class="feedback error" v-if="error.status && !phoneInputVisible">
        {{ error.message }}
      </p>
      <button class="verify" @click="verifyPhone" :disabled="pinLoading" v-if="!phoneInputVisible">
        <div v-if="pinLoading" class="loader buttonLoader"></div>
        <template v-if="!pinLoading"> {{ t('phoneVerification.verify') }} </template>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { useI18n } from 'vue-i18n'

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

import formattingMixins from '@/mixins/formattingMixins'

import Verification from './Verification.vue'

import modalMixins from '@/mixins/modalMixins.js'

import customersApi from '@/api/customersApi.js'
import IntlTelInput from '@/components/Elements/IntlTelInput.vue'
import { useFlag } from '@unleash/proxy-client-vue'

export default {
  setup() {
    const { t } = useI18n()
    const enableInternationalPhoneInput = useFlag('enable_international_phone_input', { fallback: false })
    return {
      t,
      v$: useVuelidate(),
      enableInternationalPhoneInput,
    }
  },
  data() {
    return {
      pin: '',
      phone: '',
      phoneInputVisible: false,
      codeIsValid: true,
      error: {
        status: false,
        message: '',
      },
      pinLoading: false,
      validPhoneNumberError: false,
      validPhoneNumberErrorMessage: '',
      phoneCountryCode: null,
    }
  },
  components: {
    Verification,
    IntlTelInput,
  },
  validations() {
    return {
      phone: this.enableInternationalPhoneInput ? {} : { required },
    }
  },
  mixins: [modalMixins, formattingMixins],
  computed: {
    ...mapState(['customerData', 'buttonLoader', 'cookies', 'branding']),
  },
  watch: {
    phone(newValue) {
      // Clear validation errors when phone is empty or only contains formatting characters
      if (!newValue || newValue.replace(/[^\d]/g, '').length === 0) {
        this.validPhoneNumberError = false
        this.validPhoneNumberErrorMessage = ''
        if (this.v$ && this.v$.phone) {
          this.v$.phone.$reset()
        }
      }
    },
  },
  methods: {
    ...mapMutations(['SET_TOASTER', 'SET_CUSTOMER_DATA', 'SET_PHONE_VERIFIED', 'SET_BUTTON_LOADER', 'SET_COOKIES']),
    togglePhoneInput() {
      this.error.status = false
      this.validPhoneNumberError = false
      this.validPhoneNumberErrorMessage = ''
      this.phone = ''
      this.codeIsValid = true // Clear invalid pin code error
      // Reset Vuelidate validation state
      if (this.v$ && this.v$.phone) {
        this.v$.phone.$reset()
      }
      this.phoneInputVisible = !this.phoneInputVisible
    },
    checkValidation() {
      return Object.values(this.pin).join('').length === 6
    },
    closeModal() {
      this.$emit('closeModal')
    },
    onCompleteHandler(e) {
      this.pin = e
      this.codeIsValid = true // Reset invalid pin code error when user types
    },
    onPasteHandler(e) {
      this.pin = e
      this.codeIsValid = true // Reset invalid pin code error when user pastes
    },
    sendVerificationCode(resend) {
      let payload = {
        phone: this.customerData.phone,
      }
      // Add country_code to payload if using international phone input
      if (this.enableInternationalPhoneInput && this.customerData?.country_code) {
        payload.country_code = this.customerData.country_code
      }
      customersApi
        .sendVerificationCode(payload)
        .then((response) => {
          if (response.data.data.ok) {
            if (resend) {
              this.codeIsValid = true // Reset invalid pin code error when resending
              let toasterData = {
                show: true,
                message: this.t('phoneVerification.pinSentAgain'),
              }
              this.SET_TOASTER(toasterData)
            }
          }
          this.SET_COOKIES(response.data.cookies)
        })
        .catch((error) => {
          console.log(error)
          this.error.status = true
          this.error.message = error?.response?.data?.data?.error || this.t('common.somethingWentWrong')
        })
    },
    verifyPhone() {
      this.codeIsValid = true // Reset invalid pin code error when starting verification
      if (this.pin.length != 6) {
        this.codeIsValid = false
        return
      }
      this.pinLoading = true
      this.error.status = false
      this.error.message = ''

      let payload = {
        phone: this.customerData.phone,
        security_code: this.pin,
      }

      customersApi
        .verifyPhone(payload)
        .then((response) => {
          if (response.data.data.ok) {
            this.SET_PHONE_VERIFIED(true)
            localStorage.setItem('phoneVerified', true)
            this.getCustomerInfo()
          } else if (!response.data.data.ok) {
            this.pinLoading = false
            this.error.status = true
            this.error.message = this.t('phoneVerification.verificationFailed')
          }
          this.SET_COOKIES(response.data.cookies)
        })
        .catch((error) => {
          this.pinLoading = false
          this.error.status = true
          this.error.message = error?.response?.data?.data?.error || this.t('common.somethingWentWrong')
        })
    },
    getCustomerInfo() {
      customersApi
        .getCustomerInfo()
        .then((response) => {
          this.SET_CUSTOMER_DATA(response.data.data)
          this.pinLoading = false
          this.$emit('closeModal')
          let toasterData = {
            show: true,
            message: this.t('phoneVerification.phoneVerified'),
          }
          this.SET_TOASTER(toasterData)
          this.SET_COOKIES(response.data.cookies)
        })
        .catch(() => {
          this.pinLoading = false
          this.loading = false
          this.error.status = true
          this.error.message = this.t('common.somethingWentWrong')
        })
    },
    changePhone() {
      // check if all fields are valid
      this.v$.$touch()

      if (this.enableInternationalPhoneInput) {
        const intlInputComponent = this.$refs.intlPhoneInput
        if (intlInputComponent && typeof intlInputComponent.setTouched === 'function') {
          intlInputComponent.setTouched(true)
        }
      }

      if (this.enableInternationalPhoneInput) {
        const intlInputComponent = this.$refs.intlPhoneInput
        if (!intlInputComponent || !intlInputComponent.validate(true, true)) {
          return
        }
        const countryData = intlInputComponent.getSelectedCountryData()
        if (countryData && countryData.iso2) {
          this.phoneCountryCode = countryData.iso2.toUpperCase()
        }
        this.validPhoneNumberError = false
      } else {
        if (this.v$.$error) {
          return
        }

        // Check if phone is empty
        if (!this.phone || this.phone.trim() === '') {
          this.v$.phone.$touch()
          return
        }

        // Check phone validation before sending code
        if (!this.isValidPhoneNumber(this.phone)) {
          this.validPhoneNumberError = true
          this.validPhoneNumberErrorMessage = this.t('phoneVerification.validation.validPhoneNoZeroOne')
          return
        }
        this.validPhoneNumberError = false
        this.validPhoneNumberErrorMessage = ''

        // Check phone length for non-international input
        if (this.phone.length > 0 && this.phone.length < 14) {
          this.validPhoneNumberError = true
          this.validPhoneNumberErrorMessage = this.t('phoneVerification.validation.validPhone')
          return
        }
        this.phoneCountryCode = null
      }

      this.SET_BUTTON_LOADER(true)

      // Prepare phone number for payload
      let phoneNumber = ''

      // If using international phone input, get the national number (without country code)
      if (this.enableInternationalPhoneInput) {
        const intlInputComponent = this.$refs.intlPhoneInput
        if (intlInputComponent && typeof intlInputComponent.getNationalNumber === 'function') {
          // Use the component's method to get national number (without country code)
          phoneNumber = intlInputComponent.getNationalNumber()
        } else {
          // Fallback: manually extract national number
          phoneNumber = this.phone.replace(/\D/g, '')
          const countryData = intlInputComponent?.getSelectedCountryData()
          if (countryData && countryData.dialCode && phoneNumber.startsWith(countryData.dialCode)) {
            phoneNumber = phoneNumber.substring(countryData.dialCode.length)
          }
        }
      } else {
        // For non-international input, just remove non-digits
        phoneNumber = this.phone.replace(/\D/g, '')
      }

      let customerID = localStorage.getItem('customerId')
      let payload = {
        email: this.customerData.email,
        first_name: this.customerData.first_name,
        last_name: this.customerData.last_name,
        phone: phoneNumber,
        birthdate: this.customerData.birthdate,
        email_optin: true,
      }

      // Add country_code to payload if using international phone input
      if (this.enableInternationalPhoneInput && this.phoneCountryCode) {
        payload.country_code = this.phoneCountryCode
      }

      customersApi
        .updateCustomer(payload, customerID)
        .then((response) => {
          this.SET_CUSTOMER_DATA(response.data.data)
          this.SET_BUTTON_LOADER(false)
          this.SET_COOKIES(response.data.cookies)
          let toasterData = {
            show: true,
            message: this.t('phoneVerification.codeSentToNewPhone'),
          }
          this.SET_TOASTER(toasterData)
          this.sendVerificationCode()
          this.togglePhoneInput()
        })
        .catch((error) => {
          if (error?.response?.data?.status == 403) {
            let toasterData = {
              show: true,
              message: this.t('common.sessionExpired'),
            }
            this.SET_TOASTER(toasterData)
            this.$emit('logout')
          }
          this.SET_BUTTON_LOADER(false)
          this.error.status = true
          this.error.message = error?.response?.data?.data?.error || this.t('common.somethingWentWrong')
        })
    },
    handleCountryChange(countryData) {
      if (countryData && countryData.iso2) {
        this.phoneCountryCode = countryData.iso2.toUpperCase()
      } else {
        this.phoneCountryCode = null
      }
    },
  },
  created() {
    this.sendVerificationCode()
  },
}
</script>

<style src="../../../assets/css/Modals/AuthenticationModals/PhoneVerificationModal.css" scoped></style>
